import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { suggestDestinations } from "../lib/api";

/**
 * Intelligent destination autocomplete.
 * - Instant suggestions while typing (debounced 150ms)
 * - Case-insensitive, typo- and whitespace-tolerant (server-side fuzzy match)
 * - Full keyboard support: ArrowUp/Down, Enter, Escape
 */
export default function DestinationAutocomplete({
  value,
  onChange,
  placeholder = "Japan, Amalfi Coast, Kenya, Kerala…",
  testId = "destination-autocomplete-input",
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const timer = useRef(null);
  const rootRef = useRef(null);
  const seq = useRef(0);
  const suppressNextFetch = useRef(false);

  const fetchSuggestions = (q) => {
    const id = ++seq.current;
    suggestDestinations(q)
      .then((res) => {
        if (id !== seq.current) return; // stale response
        const list = res.suggestions || [];
        setItems(list);
        setOpen(list.length > 0);
        setActive(-1);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (suppressNextFetch.current) {
      suppressNextFetch.current = false;
      return;
    }
    clearTimeout(timer.current);
    if (!value || !value.trim()) {
      setItems([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(() => fetchSuggestions(value), 150);
    return () => clearTimeout(timer.current);
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (label) => {
    suppressNextFetch.current = true;
    onChange(label);
    setOpen(false);
    setItems([]);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === "Enter") {
      if (active >= 0) {
        e.preventDefault();
        pick(items[active]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative" data-testid="destination-autocomplete">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => items.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls="destination-suggestions"
        data-testid={testId}
      />
      {open && (
        <ul
          id="destination-suggestions"
          role="listbox"
          data-testid="destination-suggestions"
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-72 overflow-auto rounded-xl border border-[var(--line)] bg-[var(--surface)] py-1.5 shadow-[0_16px_40px_rgba(20,24,32,0.14)] backdrop-blur-sm"
        >
          {items.map((label, i) => {
            const [place, ...rest] = label.split(",");
            return (
              <li
                key={label}
                role="option"
                aria-selected={i === active}
                data-testid={`destination-suggestion-${i}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(label);
                }}
                onMouseEnter={() => setActive(i)}
                className={`flex cursor-pointer items-center gap-2.5 px-4 py-2.5 text-sm transition-colors duration-150 ${
                  i === active
                    ? "bg-[rgba(176,141,87,0.12)] text-[var(--ink)]"
                    : "text-[var(--ink-2)]"
                }`}
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 text-[var(--brass)]" />
                <span className="truncate">
                  <span className="font-medium text-[var(--ink)]">{place.trim()}</span>
                  {rest.length > 0 && (
                    <span className="text-[var(--muted-ink)]">, {rest.join(",").trim()}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
